import { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getIndustrialWorkflowsContext } from '../../industrialWorkflows/services/industrialWorkflowsService';
import { getIndustrialCostContext } from '../services/industrialCostService';
import { useSnackbar } from 'notistack';
import {
  createIndustrialCostPlantUserConfig,
  deactivateIndustrialCostPlantUserConfig,
  listIndustrialCostPlantUsersConfig,
  listIndustrialCostProductionLinesConfig,
  resetIndustrialCostPlantUserPasswordConfig,
  updateIndustrialCostPlantUserConfig,
} from '../services/industrialCostService';
import type {
  CreatePlantUserPayload,
  PlantUser,
  PlantUserAccessRole,
  PlantUserPermissionsOverride,
  ProductionLineConfig,
  ResetPlantUserPasswordPayload,
  UpdatePlantUserPayload,
} from '../types/industrialCost';

// ---------------------------------------------------------------------------
// Tipos locales
// ---------------------------------------------------------------------------

type DialogMode = 'create' | 'edit';

type PlantUserForm = {
  access_id?: string;
  email: string;
  password: string;
  full_name: string;
  role: PlantUserAccessRole;
  is_active: boolean;
  permissions_override: PlantUserPermissionsOverride;
  allowed_production_line_config_ids: string[];
};

type PlantUserPasswordForm = {
  access_id: string;
  email: string;
  new_password: string;
};

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const emptyPlantUserForm: PlantUserForm = {
  email: '',
  password: '',
  full_name: '',
  role: 'factory_operator',
  is_active: true,
  permissions_override: {},
  allowed_production_line_config_ids: [],
};

const emptyPlantUserPasswordForm: PlantUserPasswordForm = {
  access_id: '',
  email: '',
  new_password: '',
};

const plantUserRoleLabels: Record<PlantUserAccessRole, string> = {
  admin: 'Administrador industrial',
  factory_manager: 'Responsable de fábrica',
  factory_operator: 'Operario',
};

const factoryManagerBasePermissions = new Set([
  'industrial:access',
  'factory_control:view',
  'production_history:view',
  'production_performance:view',
  'production_reports:view',
  'production_reports:operate',
  'production_reports:validate',
  'production_planning:manage',
  'results:view',
]);

const configurablePlantUserPermissions = [
  { key: 'factory_control:view', label: 'Ver control de fábrica', group: 'Fábrica' },
  { key: 'production_history:view', label: 'Ver histórico', group: 'Fábrica' },
  { key: 'production_performance:view', label: 'Ver rendimiento', group: 'Fábrica' },
  { key: 'production_reports:view', label: 'Ver partes', group: 'Fábrica' },
  { key: 'production_reports:operate', label: 'Operar partes', group: 'Fábrica' },
  { key: 'production_reports:validate', label: 'Validar / reabrir partes', group: 'Fábrica' },
  { key: 'production_planning:manage', label: 'Gestionar planificador de producción', group: 'Fábrica' },
  { key: 'plant_config:view', label: 'Ver configuración de planta', group: 'Fábrica' },
  { key: 'plant_config:manage', label: 'Modificar configuración de planta', group: 'Fábrica' },
  { key: 'inventory:manage', label: 'Gestión de inventario', group: 'Fábrica' },
  { key: 'costs:view', label: 'Ver costes', group: 'Costes' },
  { key: 'costs:manage', label: 'Gestionar costes', group: 'Costes' },
  { key: 'results:view', label: 'Ver resultados', group: 'Costes' },
  { key: 'imports:manage', label: 'Gestionar importaciones', group: 'Costes' },
  { key: 'workflows:access', label: 'Acceder al organizador', group: 'Organizador' },
  { key: 'workflows:processes:view', label: 'Ver flujos y etapas', group: 'Organizador' },
  { key: 'workflows:processes:manage', label: 'Gestionar flujos y etapas', group: 'Organizador' },
  { key: 'workflows:cases:view', label: 'Ver expedientes', group: 'Organizador' },
  { key: 'workflows:cases:manage', label: 'Gestionar expedientes', group: 'Organizador' },
  { key: 'workflows:tasks:view', label: 'Ver tareas', group: 'Organizador' },
  { key: 'workflows:tasks:operate', label: 'Operar tareas y partes propias', group: 'Organizador' },
  { key: 'workflows:tasks:manage', label: 'Gestionar tareas', group: 'Organizador' },
  { key: 'workflows:review:view', label: 'Ver revisión', group: 'Organizador' },
  { key: 'workflows:config:manage', label: 'Gestionar configuración', group: 'Organizador' },
  { key: 'plant_users:manage', label: 'Gestionar usuarios', group: 'Usuarios' },
];

const permissionGroups = [
  { key: 'Fábrica', label: 'Fábrica' },
  { key: 'Costes', label: 'Costes' },
  { key: 'Organizador', label: 'Organizador' },
  { key: 'Usuarios', label: 'Usuarios' },
];

const workflowOperatorPermissionKeys = [
  'workflows:access',
  'workflows:tasks:view',
  'workflows:tasks:operate',
];

const workflowReadOnlyPermissionKeys = [
  'workflows:access',
  'workflows:processes:view',
  'workflows:cases:view',
  'workflows:tasks:view',
  'workflows:review:view',
];

const workflowFullManagementPermissionKeys = [
  'workflows:access',
  'workflows:processes:view',
  'workflows:processes:manage',
  'workflows:cases:view',
  'workflows:cases:manage',
  'workflows:tasks:view',
  'workflows:tasks:operate',
  'workflows:tasks:manage',
  'workflows:review:view',
  'workflows:config:manage',
];

const workflowManagementOnlyPermissionKeys =
  workflowFullManagementPermissionKeys.filter(
    (permission) =>
      !workflowReadOnlyPermissionKeys.includes(permission),
  );

// ---------------------------------------------------------------------------
// Helpers de permisos
// ---------------------------------------------------------------------------

function normalizePlantUserPermissionsOverride(
  override: PlantUserPermissionsOverride | null | undefined,
): PlantUserPermissionsOverride {
  const allowedKeys = new Set(configurablePlantUserPermissions.map((p) => p.key));

  const allow = Array.from(
    new Set((override?.allow ?? []).filter((p) => allowedKeys.has(p))),
  );

  const deny = Array.from(
    new Set((override?.deny ?? []).filter((p) => allowedKeys.has(p))),
  ).filter((p) => !allow.includes(p));

  return { allow, deny };
}

function hasCustomPlantUserPermissions(override: PlantUserPermissionsOverride | null | undefined) {
  const normalized = normalizePlantUserPermissionsOverride(override);
  return Boolean(normalized.allow?.length || normalized.deny?.length);
}

function isIndustrialWorkflowPermission(permission: string) {
  return permission.startsWith('workflows:');
}

function resolvePlantUserPermissionsOverrideForRole(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
): PlantUserPermissionsOverride {
  if (role === 'admin') return {};

  const normalized = normalizePlantUserPermissionsOverride(override);

  if (role === 'factory_operator') {
    return {
      allow: (normalized.allow ?? []).filter(isIndustrialWorkflowPermission),
      deny: (normalized.deny ?? []).filter(isIndustrialWorkflowPermission),
    };
  }

  return normalized;
}

function resolvePlantUserPermissionEnabled(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
  permission: string,
) {
  if (role === 'admin') return true;

  const normalized = resolvePlantUserPermissionsOverrideForRole(role, override);
  if (normalized.allow?.includes(permission)) return true;
  if (normalized.deny?.includes(permission)) return false;

  if (role === 'factory_operator') {
    return permission === 'production_reports:operate';
  }

  return factoryManagerBasePermissions.has(permission);
}

function buildNextPlantUserPermissionsOverride(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
  permission: string,
  enabled: boolean,
): PlantUserPermissionsOverride {
  if (role === 'admin') return {};

  const normalized = resolvePlantUserPermissionsOverrideForRole(role, override);

  if (role === 'factory_operator' && !isIndustrialWorkflowPermission(permission)) {
    return normalized;
  }

  const baseEnabled =
    role === 'factory_operator'
      ? permission === 'production_reports:operate'
      : factoryManagerBasePermissions.has(permission);

  const allow = new Set(normalized.allow ?? []);
  const deny = new Set(normalized.deny ?? []);

  allow.delete(permission);
  deny.delete(permission);

  if (enabled !== baseEnabled) {
    if (enabled) {
      allow.add(permission);
    } else {
      deny.add(permission);
    }
  }

  return { allow: Array.from(allow), deny: Array.from(deny) };
}

function resolveWorkflowOperatorPermissionEnabled(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
) {
  return workflowOperatorPermissionKeys.every((permission) =>
    resolvePlantUserPermissionEnabled(role, override, permission),
  );
}

function buildNextWorkflowOperatorPermissionsOverride(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
  enabled: boolean,
): PlantUserPermissionsOverride {
  return workflowOperatorPermissionKeys.reduce(
    (currentOverride, permission) =>
      buildNextPlantUserPermissionsOverride(
        role,
        currentOverride,
        permission,
        enabled,
      ),
    resolvePlantUserPermissionsOverrideForRole(role, override),
  );
}

function resolveWorkflowReadOnlyPermissionEnabled(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
) {
  return workflowReadOnlyPermissionKeys.every((permission) =>
    resolvePlantUserPermissionEnabled(role, override, permission),
  );
}

function resolveWorkflowFullManagementPermissionEnabled(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
) {
  return workflowFullManagementPermissionKeys.every((permission) =>
    resolvePlantUserPermissionEnabled(role, override, permission),
  );
}

function buildNextWorkflowReadOnlyPermissionsOverride(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
  enabled: boolean,
): PlantUserPermissionsOverride {
  const permissionKeys = enabled
    ? workflowReadOnlyPermissionKeys
    : workflowFullManagementPermissionKeys;

  return permissionKeys.reduce(
    (currentOverride, permission) =>
      buildNextPlantUserPermissionsOverride(
        role,
        currentOverride,
        permission,
        enabled,
      ),
    resolvePlantUserPermissionsOverrideForRole(role, override),
  );
}

function buildNextWorkflowFullManagementPermissionsOverride(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
  enabled: boolean,
): PlantUserPermissionsOverride {
  const permissionKeys = enabled
    ? workflowFullManagementPermissionKeys
    : workflowManagementOnlyPermissionKeys;

  return permissionKeys.reduce(
    (currentOverride, permission) =>
      buildNextPlantUserPermissionsOverride(
        role,
        currentOverride,
        permission,
        enabled,
      ),
    resolvePlantUserPermissionsOverrideForRole(role, override),
  );
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'No se pudo completar la operación.';
}

function groupProductionLinesByProcessGroup(lines: ProductionLineConfig[]) {
  const groups = new Map<string, ProductionLineConfig[]>();

  lines.forEach((line) => {
    const groupKey = line.process_group?.trim() || 'Sin clasificación';
    const existing = groups.get(groupKey) ?? [];
    groups.set(groupKey, [...existing, line]);
  });

  return Array.from(groups.entries()).map(([groupLabel, groupLines]) => ({
    groupLabel,
    lines: [...groupLines].sort((a, b) => {
      const displayOrderDelta = (a.display_order ?? 0) - (b.display_order ?? 0);

      if (displayOrderDelta !== 0) return displayOrderDelta;

      return `${a.code} ${a.name}`.localeCompare(`${b.code} ${b.name}`);
    }),
  }));
}

function buildAllowedProductionLineIdsForRole(
  role: PlantUserAccessRole,
  lineIds: string[],
) {
  if (role !== 'factory_operator') return [];

  return Array.from(new Set(lineIds.filter(Boolean)));
}

// ---------------------------------------------------------------------------
// Subcomponentes
// ---------------------------------------------------------------------------

function ActiveChip({ active }: { active: boolean }) {
  return (
    <Chip
      size="small"
      label={active ? 'Activo' : 'Inactivo'}
      color={active ? 'success' : 'default'}
      variant="outlined"
    />
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function IndustrialCostPlantUsersView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [plantUserDialogOpen, setPlantUserDialogOpen] = useState(false);
  const [plantUserDialogMode, setPlantUserDialogMode] = useState<DialogMode>('create');
  const [plantUserForm, setPlantUserForm] = useState<PlantUserForm>(emptyPlantUserForm);

  const [plantUserPasswordDialogOpen, setPlantUserPasswordDialogOpen] = useState(false);
  const [plantUserPasswordForm, setPlantUserPasswordForm] = useState<PlantUserPasswordForm>(
    emptyPlantUserPasswordForm,
  );

  // -------------------------------------------------------------------------
  // Query
  // -------------------------------------------------------------------------

  const contextQuery = useQuery({
    queryKey: ['industrial-cost-context'],
    queryFn: getIndustrialCostContext,
    retry: false,
  });

  const industrialWorkflowsContextQuery = useQuery({
    queryKey: ['industrial-workflows-context'],
    queryFn: getIndustrialWorkflowsContext,
    retry: false,
  });

  const activeUserRole = contextQuery.data?.role ?? null;
  const isAdmin = activeUserRole === 'admin';
  const activeUserPermissions = new Set([
    ...(contextQuery.data?.permissions ?? []),
    ...(industrialWorkflowsContextQuery.data?.permissions ?? []),
  ]);

  const plantUsersQuery = useQuery<PlantUser[]>({
    queryKey: ['industrial-cost-production-config-plant-users'],
    queryFn: listIndustrialCostPlantUsersConfig,
  });

  const productionLinesConfigQuery = useQuery<ProductionLineConfig[]>({
    queryKey: ['industrial-cost-production-config-lines-active'],
    queryFn: async () => {
      const rows = await listIndustrialCostProductionLinesConfig();
      return rows.filter((line) => line.is_active);
    },
  });

  const plantUsers: PlantUser[] = plantUsersQuery.data ?? [];
  const productionLinesConfig = productionLinesConfigQuery.data ?? [];
  const productionLineGroups = groupProductionLinesByProcessGroup(productionLinesConfig);

  const invalidatePlantUsers = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-production-config-plant-users'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-production-report-operator-context'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-production-reports-factory-control'],
      }),
    ]);
  };

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const savePlantUserMutation = useMutation({
    mutationFn: (payload: PlantUserForm) => {
      if (payload.access_id) {
        const updatePayload: UpdatePlantUserPayload = {
          full_name: payload.full_name.trim() || null,
          role: payload.role,
          is_active: payload.is_active,
          permissions_override: resolvePlantUserPermissionsOverrideForRole(
            payload.role,
            payload.permissions_override,
          ),
          allowed_production_line_config_ids: buildAllowedProductionLineIdsForRole(
            payload.role,
            payload.allowed_production_line_config_ids,
          ),
        };
        return updateIndustrialCostPlantUserConfig(payload.access_id, updatePayload);
      }

      const createPayload: CreatePlantUserPayload = {
        email: payload.email.trim().toLowerCase(),
        password: payload.password,
        full_name: payload.full_name.trim() || null,
        role: payload.role,
        is_active: payload.is_active,
        permissions_override: resolvePlantUserPermissionsOverrideForRole(
          payload.role,
          payload.permissions_override,
        ),
        allowed_production_line_config_ids: buildAllowedProductionLineIdsForRole(
          payload.role,
          payload.allowed_production_line_config_ids,
        ),
      };
      return createIndustrialCostPlantUserConfig(createPayload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Usuario de planta guardado correctamente.', { variant: 'success' });
      setPlantUserDialogOpen(false);
      setPlantUserForm(emptyPlantUserForm);
      await invalidatePlantUsers();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const deactivatePlantUserMutation = useMutation<PlantUser, unknown, string>({
    mutationFn: (accessId: string) => deactivateIndustrialCostPlantUserConfig(accessId),
    onSuccess: async () => {
      enqueueSnackbar('Acceso de usuario de planta desactivado.', { variant: 'success' });
      await invalidatePlantUsers();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const resetPlantUserPasswordMutation = useMutation({
    mutationFn: ({
      accessId,
      payload,
    }: {
      accessId: string;
      payload: ResetPlantUserPasswordPayload;
    }) => resetIndustrialCostPlantUserPasswordConfig(accessId, payload),
    onSuccess: async () => {
      enqueueSnackbar('Contraseña actualizada correctamente.', { variant: 'success' });
      setPlantUserPasswordDialogOpen(false);
      setPlantUserPasswordForm(emptyPlantUserPasswordForm);
      await invalidatePlantUsers();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const openCreatePlantUser = () => {
    setPlantUserDialogMode('create');
    setPlantUserForm(emptyPlantUserForm);
    setPlantUserDialogOpen(true);
  };

  const openEditPlantUser = (user: PlantUser) => {
    setPlantUserDialogMode('edit');
    setPlantUserForm({
      access_id: user.access_id,
      email: user.email ?? '',
      password: '',
      full_name: user.full_name ?? '',
      role: user.role,
      is_active: user.is_active,
      permissions_override: normalizePlantUserPermissionsOverride(user.permissions_override),
      allowed_production_line_config_ids: user.allowed_production_line_config_ids ?? [],
    });
    setPlantUserDialogOpen(true);
  };

  const openResetPlantUserPassword = (user: PlantUser) => {
    setPlantUserPasswordForm({
      access_id: user.access_id,
      email: user.email ?? '',
      new_password: '',
    });
    setPlantUserPasswordDialogOpen(true);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Stack spacing={3}>
      {/* Cabecera */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.72)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <PeopleRoundedIcon sx={{ color: '#38bdf8' }} />
            <Typography sx={{ color: '#f8fafc', fontSize: '1.55rem', fontWeight: 950 }}>
              Usuarios de planta
            </Typography>
          </Stack>
          <Typography sx={{ color: '#94a3b8', maxWidth: 700 }}>
            Gestiona los usuarios del tenant industrial: alta, roles, permisos y contraseñas.
          </Typography>
        </Stack>
      </Paper>

      {/* Tabla de usuarios */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.72)',
          px: { xs: 2, md: 2.5 },
          py: { xs: 2, md: 2.5 },
        }}
      >
        <Stack spacing={2}>
          {/* Encabezado de sección */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            spacing={1.5}
          >
            <Stack spacing={0.4}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.05rem' }}>
                Usuarios de planta
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.92rem' }}>
                Gestiona usuarios reales del tenant industrial: alta de usuarios, permisos,
                activación y reset de contraseña.
              </Typography>
            </Stack>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openCreatePlantUser}
            >
              Crear usuario
            </Button>
          </Stack>

          <Alert severity="info" variant="outlined">
            Solo se muestran y gestionan usuarios vinculados a este tenant industrial. Las acciones
            se aplican siempre sobre usuarios del tenant actual.
          </Alert>

          {plantUsersQuery.isFetching && <LinearProgress />}

          {plantUsers.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No hay usuarios de planta configurados.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {plantUsers.map((user) => (
                <Paper
                  key={user.access_id}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(2,6,23,0.28)',
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    spacing={1.25}
                  >
                    <Stack spacing={0.4}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        {user.full_name || user.email || 'Usuario de planta'}
                      </Typography>

                      <Typography sx={{ color: '#cbd5e1', fontSize: '0.92rem' }}>
                        {user.email || 'Email no disponible'}
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <ActiveChip active={user.is_active} />
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                          {plantUserRoleLabels[user.role]}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        <Chip
                          size="small"
                          label={`${user.effective_permissions?.length ?? 0} permisos efectivos`}
                          variant="outlined"
                          sx={{ color: '#cbd5e1', borderColor: 'rgba(148,163,184,0.24)' }}
                        />
                        {user.role === 'factory_operator' ? (
                          <Chip
                            size="small"
                            label={`${user.allowed_production_line_config_ids?.length ?? 0} línea(s) asignada(s)`}
                            variant="outlined"
                            sx={{ color: '#bae6fd', borderColor: 'rgba(56,189,248,0.35)' }}
                          />
                        ) : null}
                        {hasCustomPlantUserPermissions(user.permissions_override) ? (
                          <Chip
                            size="small"
                            label="Personalizado"
                            color="info"
                            variant="outlined"
                          />
                        ) : null}
                      </Stack>

                      <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                        ID técnico: {user.user_id}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                    >
                      {(isAdmin || user.role !== 'admin') && (
                        <Button
                          size="small"
                          startIcon={<EditRoundedIcon />}
                          onClick={() => openEditPlantUser(user)}
                        >
                          Editar
                        </Button>
                      )}

                      {(isAdmin || user.role !== 'admin') && (
                        <Button size="small" onClick={() => openResetPlantUserPassword(user)}>
                          Reset contraseña
                        </Button>
                      )}

                      {user.is_active && (isAdmin || user.role !== 'admin') && (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineRoundedIcon />}
                          onClick={() => {
                            if (window.confirm('¿Desactivar este usuario de planta?')) {
                              deactivatePlantUserMutation.mutate(user.access_id);
                            }
                          }}
                        >
                          Desactivar
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Dialog: crear / editar usuario */}
      <Dialog
        open={plantUserDialogOpen}
        onClose={() => setPlantUserDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {plantUserDialogMode === 'create' ? 'Crear usuario de planta' : 'Editar usuario de planta'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="Correo electrónico"
              value={plantUserForm.email}
              onChange={(event) =>
                setPlantUserForm((current) => ({ ...current, email: event.target.value }))
              }
              disabled={plantUserDialogMode === 'edit'}
              helperText={
                plantUserDialogMode === 'edit'
                  ? 'El correo no se modifica desde esta pantalla por ahora.'
                  : 'Este correo se usará para iniciar sesión en fábrica.'
              }
              fullWidth
            />

            {plantUserDialogMode === 'create' && (
              <TextField
                size="small"
                label="Contraseña temporal"
                type="password"
                value={plantUserForm.password}
                onChange={(event) =>
                  setPlantUserForm((current) => ({ ...current, password: event.target.value }))
                }
                helperText="Debe tener al menos 8 caracteres. El usuario podrá iniciar sesión con esta contraseña."
                fullWidth
              />
            )}

            <TextField
              size="small"
              label="Nombre"
              value={plantUserForm.full_name}
              onChange={(event) =>
                setPlantUserForm((current) => ({ ...current, full_name: event.target.value }))
              }
              fullWidth
            />

            <TextField
              size="small"
              select
              label="Rol"
              value={plantUserForm.role}
              onChange={(event) => {
                const nextRole = event.target.value as PlantUserAccessRole;
                setPlantUserForm((current) => ({
                  ...current,
                  role: nextRole,
                  permissions_override: resolvePlantUserPermissionsOverrideForRole(
                    nextRole,
                    current.permissions_override,
                  ),
                  allowed_production_line_config_ids:
                    nextRole === 'factory_operator'
                      ? current.allowed_production_line_config_ids
                      : [],
                }));
              }}
              disabled={plantUserForm.role === 'admin'}
              fullWidth
            >
              <MenuItem value="factory_operator">Operario</MenuItem>
              <MenuItem value="factory_manager">Responsable de fábrica</MenuItem>
              {(isAdmin && plantUserDialogMode === 'create' && !plantUsers.some((u) => u.role === 'admin')) || plantUserForm.role === 'admin' ? (
                <MenuItem value="admin">Administrador industrial</MenuItem>
              ) : null}
            </TextField>

            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={plantUserForm.is_active}
                onChange={(event) =>
                  setPlantUserForm((current) => ({ ...current, is_active: event.target.checked }))
                }
              />
              <Typography>Activo</Typography>
            </Stack>

            {(
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(2,6,23,0.22)',
                  p: 1.5,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack spacing={0.3}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>Permisos</Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                      El rol define una base de permisos. Los accesos del organizador se conceden
                      de forma explícita para mantener separados fábrica, costes y tareas.
                    </Typography>
                  </Stack>

                  {plantUserForm.role === 'factory_operator' ? (
                    <Stack spacing={1.5}>
                      <Alert severity="info" variant="outlined">
                        El operario mantiene su acceso limitado al puesto operario de fábrica y a las
                        líneas asignadas aquí. Los accesos adicionales del organizador se conceden
                        de forma explícita.
                      </Alert>

                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: '1px solid rgba(56,189,248,0.24)',
                          bgcolor: 'rgba(8,47,73,0.20)',
                          p: 1.5,
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Stack spacing={0.25}>
                            <Typography sx={{ color: '#e0f2fe', fontWeight: 900 }}>
                              Líneas accesibles
                            </Typography>
                            <Typography sx={{ color: '#93c5fd', fontSize: '0.82rem' }}>
                              Selecciona las máquinas/líneas que este usuario podrá ver y operar.
                            </Typography>
                          </Stack>

                          {productionLinesConfigQuery.isFetching ? (
                            <LinearProgress />
                          ) : productionLinesConfig.length === 0 ? (
                            <Alert severity="warning" variant="outlined">
                              No hay líneas activas configuradas.
                            </Alert>
                          ) : (
                            <Stack spacing={1.25}>
                              {productionLineGroups.map((group) => {
                                const groupLineIds = group.lines.map((line) => line.id);
                                const selectedGroupLineIds = groupLineIds.filter((lineId) =>
                                  plantUserForm.allowed_production_line_config_ids.includes(lineId),
                                );
                                const allGroupLinesSelected =
                                  groupLineIds.length > 0 &&
                                  selectedGroupLineIds.length === groupLineIds.length;

                                return (
                                  <Paper
                                    key={group.groupLabel}
                                    elevation={0}
                                    sx={{
                                      borderRadius: 2,
                                      border: '1px solid rgba(148,163,184,0.14)',
                                      bgcolor: 'rgba(15,23,42,0.36)',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      sx={{
                                        px: 1.25,
                                        py: 0.85,
                                        bgcolor: 'rgba(15,23,42,0.42)',
                                      }}
                                    >
                                      <Stack spacing={0.15}>
                                        <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                                          {group.groupLabel}
                                        </Typography>
                                        <Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>
                                          {selectedGroupLineIds.length} de {group.lines.length} línea(s) asignada(s)
                                        </Typography>
                                      </Stack>

                                      <Switch
                                        checked={allGroupLinesSelected}
                                        onChange={(event) => {
                                          const nextChecked = event.target.checked;

                                          setPlantUserForm((current) => {
                                            const currentIds = current.allowed_production_line_config_ids;
                                            const groupLineIdSet = new Set(groupLineIds);

                                            const nextIds = nextChecked
                                              ? Array.from(new Set([...currentIds, ...groupLineIds]))
                                              : currentIds.filter((lineId) => !groupLineIdSet.has(lineId));

                                            return {
                                              ...current,
                                              allowed_production_line_config_ids: nextIds,
                                            };
                                          });
                                        }}
                                      />
                                    </Stack>

                                    <Stack spacing={0.35} sx={{ px: 1, py: 0.75 }}>
                                      {group.lines.map((line) => {
                                        const checked =
                                          plantUserForm.allowed_production_line_config_ids.includes(
                                            line.id,
                                          );

                                        return (
                                          <Stack
                                            key={line.id}
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{
                                              borderRadius: 1.5,
                                              px: 0.75,
                                              py: 0.45,
                                              bgcolor: checked
                                                ? 'rgba(34,211,238,0.10)'
                                                : 'transparent',
                                            }}
                                          >
                                            <Stack spacing={0}>
                                              <Typography
                                                sx={{
                                                  color: '#e2e8f0',
                                                  fontWeight: 850,
                                                  fontSize: '0.88rem',
                                                }}
                                              >
                                                {line.code} · {line.name}
                                              </Typography>
                                              {line.description ? (
                                                <Typography
                                                  sx={{
                                                    color: '#64748b',
                                                    fontSize: '0.74rem',
                                                    lineHeight: 1.25,
                                                  }}
                                                >
                                                  {line.description}
                                                </Typography>
                                              ) : null}
                                            </Stack>

                                            <Switch
                                              size="small"
                                              checked={checked}
                                              onChange={(event) => {
                                                const nextChecked = event.target.checked;

                                                setPlantUserForm((current) => {
                                                  const currentIds =
                                                    current.allowed_production_line_config_ids;
                                                  const nextIds = nextChecked
                                                    ? Array.from(new Set([...currentIds, line.id]))
                                                    : currentIds.filter((id) => id !== line.id);

                                                  return {
                                                    ...current,
                                                    allowed_production_line_config_ids: nextIds,
                                                  };
                                                });
                                              }}
                                            />
                                          </Stack>
                                        );
                                      })}
                                    </Stack>
                                  </Paper>
                                );
                              })}
                            </Stack>
                          )}
                        </Stack>
                      </Paper>

                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: '1px solid rgba(148,163,184,0.16)',
                          bgcolor: 'rgba(2,6,23,0.28)',
                          overflow: 'hidden',
                        }}
                      >
                        <Stack
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid rgba(148,163,184,0.12)',
                            bgcolor: 'rgba(15,23,42,0.5)',
                          }}
                        >
                          <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '0.95rem' }}>
                            Organizador
                          </Typography>
                        </Stack>

                        <Stack spacing={0} sx={{ px: 1.5, py: 0.75 }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ py: 0.5 }}
                          >
                            <Stack spacing={0.1}>
                              <Typography sx={{ color: '#cbd5e1', fontSize: '0.88rem', fontWeight: 700 }}>
                                Trabajar en tareas del organizador
                              </Typography>
                              <Typography sx={{ color: '#475569', fontSize: '0.74rem' }}>
                                Permite ver su bandeja operativa, rellenar partes asociados y completar tareas propias.
                              </Typography>
                            </Stack>

                            <Switch
                              size="small"
                              checked={resolveWorkflowOperatorPermissionEnabled(
                                plantUserForm.role,
                                plantUserForm.permissions_override,
                              )}
                              disabled={workflowOperatorPermissionKeys.some(
                                (permission) => !activeUserPermissions.has(permission),
                              )}
                              onChange={(event) =>
                                setPlantUserForm((current) => ({
                                  ...current,
                                  permissions_override: buildNextWorkflowOperatorPermissionsOverride(
                                    current.role,
                                    current.permissions_override,
                                    event.target.checked,
                                  ),
                                }))
                              }
                            />
                          </Stack>
                        </Stack>
                      </Paper>
                    </Stack>
                  ) : plantUserForm.role !== 'factory_manager' ? (
                    <Alert severity="info" variant="outlined">
                      El administrador industrial conserva todos los permisos del módulo.
                    </Alert>
                  ) : (
                    <Stack spacing={2}>
                      {permissionGroups.map((group) => {
                        if (group.key === 'Organizador') {
                          const readOnlyEnabled =
                            resolveWorkflowReadOnlyPermissionEnabled(
                              plantUserForm.role,
                              plantUserForm.permissions_override,
                            );

                          const fullManagementEnabled =
                            resolveWorkflowFullManagementPermissionEnabled(
                              plantUserForm.role,
                              plantUserForm.permissions_override,
                            );

                          return (
                            <Paper
                              key={group.key}
                              elevation={0}
                              sx={{
                                borderRadius: 3,
                                border: '1px solid rgba(148,163,184,0.16)',
                                bgcolor: 'rgba(2,6,23,0.28)',
                                overflow: 'hidden',
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  borderBottom: '1px solid rgba(148,163,184,0.12)',
                                  bgcolor: 'rgba(15,23,42,0.5)',
                                }}
                              >
                                <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '0.95rem' }}>
                                  Organizador
                                </Typography>
                              </Stack>

                              <Stack spacing={0} sx={{ px: 1.5, py: 0.75 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ py: 0.5 }}
                                >
                                  <Stack spacing={0.1}>
                                    <Typography sx={{ color: '#cbd5e1', fontSize: '0.88rem', fontWeight: 700 }}>
                                      Solo lectura del organizador
                                    </Typography>
                                    <Typography sx={{ color: '#475569', fontSize: '0.74rem' }}>
                                      Permite consultar expedientes, flujos, tareas, partes e historial sin modificar información.
                                    </Typography>
                                  </Stack>

                                  <Switch
                                    size="small"
                                    checked={readOnlyEnabled}
                                    disabled={workflowReadOnlyPermissionKeys.some(
                                      (permission) => !activeUserPermissions.has(permission),
                                    )}
                                    onChange={(event) =>
                                      setPlantUserForm((current) => ({
                                        ...current,
                                        permissions_override: buildNextWorkflowReadOnlyPermissionsOverride(
                                          current.role,
                                          current.permissions_override,
                                          event.target.checked,
                                        ),
                                      }))
                                    }
                                  />
                                </Stack>

                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{
                                    py: 0.5,
                                    borderTop: '1px solid rgba(148,163,184,0.10)',
                                  }}
                                >
                                  <Stack spacing={0.1}>
                                    <Typography sx={{ color: '#cbd5e1', fontSize: '0.88rem', fontWeight: 700 }}>
                                      Gestión completa del organizador
                                    </Typography>
                                    <Typography sx={{ color: '#475569', fontSize: '0.74rem' }}>
                                      Permite crear, modificar y gestionar expedientes, tareas, partes, flujos y configuración.
                                    </Typography>
                                  </Stack>

                                  <Switch
                                    size="small"
                                    checked={fullManagementEnabled}
                                    disabled={workflowFullManagementPermissionKeys.some(
                                      (permission) => !activeUserPermissions.has(permission),
                                    )}
                                    onChange={(event) =>
                                      setPlantUserForm((current) => ({
                                        ...current,
                                        permissions_override: buildNextWorkflowFullManagementPermissionsOverride(
                                          current.role,
                                          current.permissions_override,
                                          event.target.checked,
                                        ),
                                      }))
                                    }
                                  />
                                </Stack>
                              </Stack>
                            </Paper>
                          );
                        }

                        const groupPermissions = configurablePlantUserPermissions.filter(
                          (p) => p.group === group.key,
                        );
                        const allEnabled = groupPermissions.every((p) =>
                          resolvePlantUserPermissionEnabled(
                            plantUserForm.role,
                            plantUserForm.permissions_override,
                            p.key,
                          ),
                        );
                        return (
                          <Paper
                            key={group.key}
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.16)',
                              bgcolor: 'rgba(2,6,23,0.28)',
                              overflow: 'hidden',
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{
                                px: 1.5,
                                py: 1,
                                borderBottom: '1px solid rgba(148,163,184,0.12)',
                                bgcolor: 'rgba(15,23,42,0.5)',
                              }}
                            >
                              <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '0.95rem' }}>
                                {group.label}
                              </Typography>
                              <Switch
                                checked={allEnabled}
                                disabled={groupPermissions.every((p) => !activeUserPermissions.has(p.key))}
                                onChange={(event) => {
                                  let nextOverride = plantUserForm.permissions_override;
                                  groupPermissions
                                    .filter((p) => activeUserPermissions.has(p.key))
                                    .forEach((p) => {
                                      nextOverride = buildNextPlantUserPermissionsOverride(
                                        plantUserForm.role,
                                        nextOverride,
                                        p.key,
                                        event.target.checked,
                                      );
                                    });
                                  setPlantUserForm((current) => ({
                                    ...current,
                                    permissions_override: nextOverride,
                                  }));
                                }}
                              />
                            </Stack>
                            <Stack spacing={0} sx={{ px: 1.5, py: 0.75 }}>
                              {groupPermissions.map((permission) => {
                                const enabled = resolvePlantUserPermissionEnabled(
                                  plantUserForm.role,
                                  plantUserForm.permissions_override,
                                  permission.key,
                                );
                                const baseEnabled = factoryManagerBasePermissions.has(permission.key);

                                return (
                                  <Stack
                                    key={permission.key}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ py: 0.5 }}
                                  >
                                    <Stack spacing={0.1}>
                                      <Typography sx={{ color: '#cbd5e1', fontSize: '0.88rem', fontWeight: 700 }}>
                                        {permission.label}
                                      </Typography>
                                      <Typography sx={{ color: '#475569', fontSize: '0.74rem' }}>
                                        {baseEnabled ? 'Incluido por defecto' : 'No incluido por defecto'}
                                      </Typography>
                                    </Stack>
                                    <Switch
                                      size="small"
                                      checked={enabled}
                                      disabled={!activeUserPermissions.has(permission.key)}
                                      onChange={(event) =>
                                        setPlantUserForm((current) => ({
                                          ...current,
                                          permissions_override: buildNextPlantUserPermissionsOverride(
                                            current.role,
                                            current.permissions_override,
                                            permission.key,
                                            event.target.checked,
                                          ),
                                        }))
                                      }
                                    />
                                  </Stack>
                                );
                              })}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlantUserDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              savePlantUserMutation.isPending ||
              !plantUserForm.role ||
              !plantUserForm.email.trim() ||
              (plantUserDialogMode === 'create' && plantUserForm.password.length < 8)
            }
            onClick={() => savePlantUserMutation.mutate(plantUserForm)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: reset de contraseña */}
      <Dialog
        open={plantUserPasswordDialogOpen}
        onClose={() => setPlantUserPasswordDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resetear contraseña</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning" variant="outlined">
              Esta acción cambia la contraseña de inicio de sesión del usuario seleccionado.
            </Alert>

            <TextField
              size="small"
              label="Usuario"
              value={plantUserPasswordForm.email}
              disabled
              fullWidth
            />

            <TextField
              size="small"
              label="Nueva contraseña"
              type="password"
              value={plantUserPasswordForm.new_password}
              onChange={(event) =>
                setPlantUserPasswordForm((current) => ({
                  ...current,
                  new_password: event.target.value,
                }))
              }
              helperText="Debe tener al menos 8 caracteres."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlantUserPasswordDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              resetPlantUserPasswordMutation.isPending ||
              plantUserPasswordForm.new_password.length < 8
            }
            onClick={() =>
              resetPlantUserPasswordMutation.mutate({
                accessId: plantUserPasswordForm.access_id,
                payload: { new_password: plantUserPasswordForm.new_password },
              })
            }
          >
            Actualizar contraseña
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
